import { useReactiveVar } from '@apollo/client';

import { userStateVar } from 'core/state/userState';
import { UserRole } from 'types';

/**
 * Capability booleans for the survey surfaces, mirroring the backend's two
 * tiers. Hiding a control is a UX affordance only — the server remains the
 * authority on every query.
 *
 * The split is the point:
 *  - READING insights (aggregates, the comment feed, a student's answers,
 *    the AI digest incl. generating it, the survey list as a filter) — the
 *    survey owners PLUS HR: HR owns the conversation with a teacher and
 *    needs the words behind the numbers (UX pack 0095–0098; before it the
 *    named tier was owners-only).
 *  - BUILDING (constructor, assignments, tags) — survey owners only
 *    (backend SURVEY_MANAGER_ROLES).
 *
 * Getting this wrong is not cosmetic. A tab shown to a role the server
 * refuses reads «комментариев нет» — a refusal displayed as a fact about the
 * data; a tab hidden from a role the server admits is a feature lost.
 */
export interface ISurveyPermissions {
  /** Admin, AccountManager, Methodist, HR — surveyAnalytics / surveySignals. */
  canReadAnalytics: boolean;
  /**
   * Same set — «Обзор»: comments, AI digest (reading AND generating), the
   * students table + drawer, the survey filter (`surveys` list).
   */
  canReadInsights: boolean;
  /** Admin, AccountManager, Methodist — the survey constructor and its list. */
  canManageSurveys: boolean;
  /** = canReadInsights — the comment feed and a student's answers. */
  canReadNamedAnswers: boolean;
  /**
   * The assignment results page exists for this role — the `surveyResults`
   * route (AppRouter). Until ТЗ 0124 it was owners-only and HR saw a survey
   * title as text, not a link to a 404 (перепроверка F25); since 0124 HR
   * holds the route too (backend: SURVEY_INSIGHT_READ_ROLES). The pooled
   * `surveySummary` stays owners-only.
   */
  canOpenSurveyResults: boolean;
}

const hasAnyRole = (roles: UserRole[], allowed: UserRole[]): boolean =>
  roles.some((role) => allowed.includes(role));

/** ACCOUNTANT is this app's name for the backend's AccountManager. */
const SURVEY_MANAGER_ROLES = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.METHODIST,
];

/** Backend SURVEY_MANAGER_ROLES + HRManager (insights read tier). */
const INSIGHTS_ROLES = [...SURVEY_MANAGER_ROLES, UserRole.HRMANAGER];

export const useSurveyPermissions = (): ISurveyPermissions => {
  const userState = useReactiveVar(userStateVar);
  const roles = (userState.me?.roles ?? []) as UserRole[];

  const canManageSurveys = hasAnyRole(roles, SURVEY_MANAGER_ROLES);
  const canReadInsights = hasAnyRole(roles, INSIGHTS_ROLES);

  return {
    canReadAnalytics: canReadInsights,
    canReadInsights,
    canManageSurveys,
    canReadNamedAnswers: canReadInsights,
    canOpenSurveyResults: canReadInsights,
  };
};
